import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    CalendarIcon,
    ClockIcon,
    StarIcon,
    PencilSquareIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline";
import moment from 'moment';
import BaseModal from 'components/Base/BaseModal';
import BaseEdit from 'components/Base/BaseEdit';
import PhoneInput from 'react-phone-input-2';
import { currentUSStateCodes } from 'utils/constants';
import { Switch } from '@headlessui/react';

const PatientAbout = () => {
    const [patient, setPatient] = useState(null);
    const [activeJobs, setActiveJobs] = useState([]);
    const [jobHistory, setJobHistory] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [isEditingAllowed, setIsEditingAllowed] = useState(true);
    const [totalSpent, setTotalSpent] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [editError, setEditError] = useState({});
    const [isAddressVerified, setIsAddressVerified] = useState(true);
    const [emailNotifLoading, setEmailNotifLoading] = useState(false);
    const [originalAddress, setOriginalAddress] = useState(null);

    const { id } = useParams();

    useEffect(() => {
        axios.get(`/patients/${id}`)
            .then(({ data }) => {
                setPatient(data.patient);
            })
            .catch(error => {
                console.error('Error fetching patient:', error);
            });

        axios.get(`/patients/${id}/jobs`)
            .then(({ data }) => {

                const active = data.filter(job => job.status === 'working');
                const history = data.filter(job => job.status !== 'working');
                setActiveJobs(active);
                setJobHistory(history);
                setIsEditingAllowed(active.length === 0);

                const total = data.reduce((sum, job) => sum + (job.price || 0), 0);
                setTotalSpent(total);

                const jobReviews = data
                    .filter(job => job.review)
                    .map(job => ({
                        ...job.review,
                        jobTitle: job.title,
                        // date_create: job.review.date_create || job.date[0] 
                    }));
                console.log(jobReviews)
                setReviews(jobReviews);
            })
            .catch(error => {
                console.error('Error fetching jobs:', error);
            });
    }, [id]);

    useEffect(() => {
        if (patient) {
            setEditForm({
                name: patient.name || '',
                surname: patient.surname || '',
                email: patient.email || '',
                phone: patient.phone || '',
                about: patient.about || '',
                location: {
                    addressLine1: patient.location?.addressLine1 || '',
                    addressLine2: patient.location?.addressLine2 || '',
                    city: patient.location?.city || '',
                    state: patient.location?.state || '',
                    zipCode: patient.location?.zipCode || '',
                },
            });
            setOriginalAddress({
                addressLine1: patient.location?.addressLine1 || '',
                addressLine2: patient.location?.addressLine2 || '',
                city: patient.location?.city || '',
                state: patient.location?.state || '',
                zipCode: patient.location?.zipCode || '',
            });
        }
    }, [patient]);

    const handleEditChange = (field, value) => {
        if (field.startsWith('location.')) {
            const locField = field.split('.')[1];
            setEditForm(prev => {
                const newLocation = { ...prev.location, [locField]: value };
                if (
                    originalAddress &&
                    (
                        originalAddress.addressLine1 !== newLocation.addressLine1 ||
                        originalAddress.addressLine2 !== newLocation.addressLine2 ||
                        originalAddress.city !== newLocation.city ||
                        originalAddress.state !== newLocation.state ||
                        originalAddress.zipCode !== newLocation.zipCode
                    )
                ) {
                    setIsAddressVerified(false);
                }
                return {
                    ...prev,
                    location: newLocation,
                };
            });
        } else {
            setEditForm(prev => ({ ...prev, [field]: value }));
        }
    };

    const verifyAddress = () => {
        axios
            .post('/auth/signup/address/confirm', { location: editForm.location })
            .then(({ data }) => {
                setIsAddressVerified(data.success);
                setEditError({});
            })
            .catch((error) => {
                setIsAddressVerified(false);
                setEditError({
                    type: error.response?.data?.error || 'verification',
                    message: error.response?.data?.message || 'Failed to verify address',
                });
            });
    };

    const handleEditSubmit = (e) => {
        e.preventDefault();
        if (!editForm.name || !editForm.surname || !editForm.email || !editForm.phone || !editForm.about ||
            !editForm.location.addressLine1 || !editForm.location.city || !editForm.location.state || !editForm.location.zipCode) {
            setEditError({
                type: 'validation',
                message: 'Please fill in all required fields.',
            });
            return;
        }
        if (!isAddressVerified) {
            setEditError({
                type: 'validation',
                message: 'Please verify the address before submitting.',
            });
            return;
        }
        axios.patch(`/patients/${id}`, { ...editForm, location: { ...editForm.location, isAddressVerified } })
            .then(({ data }) => {
                setPatient(data.patient);
                setShowEditModal(false);
                setEditError({});
            })
            .catch(error => {
                setEditError({
                    type: 'server',
                    message: error.response?.data.error || 'Failed to update patient',
                });
            });
    };

    useEffect(() => {

    }, [])

    const handleEmailNotifToggle = (value) => {
        setEmailNotifLoading(true);
        axios.patch(`/patients/${id}`, { emailNotifications: value })
            .then(({ data }) => {
                setPatient(data.patient);
            })
            .finally(() => setEmailNotifLoading(false));
    };

    if (!patient) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 flex items-center justify-center">
                <div className="animate-pulse flex space-x-4">
                    <div className="h-12 w-12 rounded-full bg-slate-200"></div>
                    <div className="space-y-3">
                        <div className="h-4 w-[200px] rounded bg-slate-200"></div>
                        <div className="h-4 w-[160px] rounded bg-slate-200"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50  pb-5">
            <div className="mx-auto pt-8" style={{ maxWidth: '1100px' }}>
                {/* Header Section */}
                <div className="sm:flex sm:items-center sm:justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                            Patient Details
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            View and manage patient information
                        </p>
                    </div>
                    <div className="mt-4 sm:mt-0 flex items-center gap-4">
                        {!isEditingAllowed && (
                            <div className="flex items-center text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
                                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                <span className="text-sm">Cannot edit during active jobs</span>
                            </div>
                        )}
                        <button
                            disabled={!isEditingAllowed}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                            onClick={() => setShowEditModal(true)}
                        >
                            <PencilSquareIcon className="h-4 w-4 mr-2" />
                            Edit Profile
                        </button>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Profile Information Card */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                            <UserIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Profile Information
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1">Full Name</div>
                                    <div className="text-base text-gray-900">{patient.name} {patient.surname}</div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                                        Email
                                    </div>
                                    <div className="text-base text-gray-900">{patient.email}</div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                                        <PhoneIcon className="h-4 w-4 mr-1" />
                                        Phone
                                    </div>
                                    <div className="text-base text-gray-900">{patient.phone}</div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                                        <MapPinIcon className="h-4 w-4 mr-1" />
                                        Address
                                    </div>
                                    <div className="text-base text-gray-900">
                                        {patient.location.addressLine1}<br />
                                        {patient.location.city}, {patient.location.state} {patient.location.zipCode}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1">About</div>
                                    <div className="text-base text-gray-900">{patient.about}</div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1">Total Spent</div>
                                    <div className="text-base text-gray-900">${totalSpent.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {/* Email Notifications Toggle */}
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-gray-500">Email Notifications</span>
                                <Switch
                                    checked={!!patient.emailNotifications}
                                    onChange={handleEmailNotifToggle}
                                    disabled={emailNotifLoading}
                                    className={`${!!patient.emailNotifications ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                                >
                                    <span
                                        className={`${!!patient.emailNotifications ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                    />
                                </Switch>
                                {emailNotifLoading && <span className="ml-2 text-xs text-gray-400">Saving...</span>}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                            <CalendarIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Active Jobs
                        </h2>

                        {activeJobs.length > 0 ? (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {activeJobs.map(job => (
                                    <Link
                                        to={`/jobs/${job._id}`}
                                        key={job._id}
                                        className="relative group rounded-xl bg-sky-50 p-5 hover:bg-sky-100 transition-all duration-200"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h3 className="text-base font-semibold text-gray-900">{job.title}</h3>
                                                <p className="mt-1 text-sm text-gray-500 flex items-center">
                                                    <ClockIcon className="h-4 w-4 mr-1" />
                                                    {moment(job.date[0]).format('DD MMMM, YYYY')}
                                                    {job.date.length > 1 &&
                                                        <span>
                                                            - {moment(job?.date[1]).format('DD MMMM, YYYY')}
                                                        </span>
                                                    }
                                                </p>
                                            </div>
                                            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                {job.status}
                                            </span>
                                        </div>
                                        <div className="mt-4">
                                            <button className="text-sm text-sky-600 hover:text-sky-700 font-medium flex items-center group">
                                                View Details
                                                <ChevronRightIcon className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                                            </button>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No active jobs</h3>
                                <p className="mt-1 text-sm text-gray-500">This patient has no ongoing care services.</p>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                            <ClockIcon className="h-5 w-5 text-sky-500 mr-2" />
                            Job History
                        </h2>

                        {jobHistory.length > 0 ? (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {jobHistory.map((job) => (
                                    <div
                                        key={job._id}
                                        className="relative group rounded-xl bg-gray-50 p-5 hover:bg-gray-100 transition-all duration-200"
                                    >
                                        <Link to={`/jobs/${job._id}`} className="block">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <h3 className="text-base font-semibold text-gray-900">{job.title}</h3>
                                                    <p className="mt-1 text-sm text-gray-500 flex items-center">
                                                        <ClockIcon className="h-4 w-4 mr-1" />
                                                        {moment(job.date[0]).format("DD MMMM, YYYY")}
                                                        {job.date.length > 1 && (
                                                            <span> - {moment(job?.date[1]).format("DD MMMM, YYYY")}</span>
                                                        )}
                                                    </p>
                                                </div>
                                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                                    {job.status}
                                                </span>
                                            </div>
                                        </Link>

                                        <div className="mt-4 flex gap-3">
                                            <Link
                                                to={`/jobs/${job._id}`}
                                                className="text-sm text-gray-600 hover:text-gray-700 font-medium flex items-center group"
                                            >
                                                View Details
                                                <ChevronRightIcon className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" />
                                            </Link>

                                            {(job.status === "active" || job.status === "pending") && (
                                                <Link
                                                    to={`/jobs/${job._id}/edit`}
                                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center group"
                                                >
                                                    <PencilSquareIcon className="h-4 w-4 mr-1" />
                                                    Edit
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                ))}

                            </div>
                        ) : (
                            <div className="text-center py-6">
                                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No job history</h3>
                                <p className="mt-1 text-sm text-gray-500">This patient hasn't completed any services yet.</p>
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-2xl p-6 shadow-lg shadow-sky-900/5 ring-1 ring-slate-100">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <StarIcon className="h-5 w-5 text-sky-500 mr-2" />
                                Reviews
                            </h2>
                            {reviews.length > 0 && (
                                <div className="flex items-center bg-sky-50 px-3 py-1 rounded-lg">
                                    <span className="text-sm font-medium text-sky-700">
                                        {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}
                                    </span>
                                </div>
                            )}
                        </div>

                        {reviews.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {reviews.map((review, index) => (
                                    <div
                                        key={index}
                                        className="rounded-xl bg-gradient-to-b from-gray-50 to-white p-5 shadow-sm ring-1 ring-gray-100 transition-all duration-200 hover:shadow-md"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900 mb-1">
                                                    {review.jobTitle}
                                                </h4>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center">
                                                        {[...Array(5)].map((_, i) => (
                                                            <StarIcon
                                                                key={i}
                                                                className={`h-4 w-4 ${i < review.rate ? 'text-yellow-400' : 'text-gray-200'}`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-xs font-medium text-gray-500">
                                                        {review.rate}.0
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center px-2 py-1 rounded-md bg-gray-50 ring-1 ring-gray-100">
                                                <CalendarIcon className="h-3 w-3 text-gray-400 mr-1" />
                                                <time className="text-xs text-gray-500" dateTime={review.date_create}>
                                                    {moment(review.date_create).format('MMM D, YYYY')}
                                                </time>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <div className="absolute left-0 top-0 select-none text-4xl text-gray-100 -translate-y-2 -translate-x-1">"</div>
                                            <p className="text-gray-600 text-sm relative pl-3">{review.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 px-4">
                                <div className="bg-gray-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                    <StarIcon className="h-8 w-8 text-gray-400" />
                                </div>
                                <h3 className="text-base font-medium text-gray-900 mb-1">No reviews yet</h3>
                                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                                    This patient hasn't received any reviews from completed services.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <BaseModal show={showEditModal} onClose={() => setShowEditModal(false)} width={600}>
                <form onSubmit={handleEditSubmit} className="space-y-6 p-4">
                    <h2 className="text-xl font-semibold mb-4">Edit Patient Profile</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                            <input
                                type="text"
                                value={editForm?.name || ''}
                                onChange={e => handleEditChange('name', e.target.value)}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                placeholder="Enter first name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                            <input
                                type="text"
                                value={editForm?.surname || ''}
                                onChange={e => handleEditChange('surname', e.target.value)}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                placeholder="Enter last name"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><EnvelopeIcon className="h-4 w-4 mr-1" />Email</label>
                            <input
                                type="email"
                                value={editForm?.email || ''}
                                onChange={e => handleEditChange('email', e.target.value)}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                placeholder="patient@example.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center"><PhoneIcon className="h-4 w-4 mr-1" />Phone</label>
                            <PhoneInput
                                country={'us'}
                                value={editForm?.phone || ''}
                                specialLabel={''}
                                onChange={phone => handleEditChange('phone', phone)}
                                containerClass="phone-input-container"
                                inputClass="!w-full !h-[38px] !rounded-lg !py-2 !px-3 !text-sm !border-gray-300"
                                buttonClass="!rounded-l-lg !h-[38px]"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">About</label>
                        <BaseEdit
                            name={'about'}
                            value={editForm?.about || ''}
                            onChange={about => handleEditChange('about', about)}
                            className="min-h-[120px]"
                        />
                    </div>
                    <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input
                            type="text"
                            value={editForm?.location?.addressLine1 || ''}
                            onChange={e => handleEditChange('location.addressLine1', e.target.value)}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm mb-2"
                            placeholder="Street address"
                        />
                        <input
                            type="text"
                            value={editForm?.location?.addressLine2 || ''}
                            onChange={e => handleEditChange('location.addressLine2', e.target.value)}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm mb-2"
                            placeholder="Apartment, suite, etc. (optional)"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input
                                type="text"
                                value={editForm?.location?.city || ''}
                                onChange={e => handleEditChange('location.city', e.target.value)}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                placeholder="City"
                            />
                            <select
                                value={editForm?.location?.state || ''}
                                onChange={e => handleEditChange('location.state', e.target.value)}
                                className="block w-full h-[38px] rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm pl-2"
                            >
                                <option value="" className="py-2">Select a state</option>
                                {Object.entries(currentUSStateCodes).map(([code, name]) => (
                                    <option key={code} value={code} className="py-2">{name}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                value={editForm?.location?.zipCode || ''}
                                onChange={e => handleEditChange('location.zipCode', e.target.value)}
                                className="block w-full h-[38px] rounded-lg border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                                placeholder="ZIP Code"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={verifyAddress}
                            disabled={!['addressLine1', 'city', 'state', 'zipCode'].every(key => editForm?.location?.[key]) || isAddressVerified}
                            className={`flex items-center h-[38px] px-4 rounded-lg text-sm font-medium transition-all duration-200 min-w-[120px] justify-center ${isAddressVerified
                                ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20'
                                : 'bg-sky-50 text-sky-700 ring-1 ring-sky-600/20 hover:bg-sky-100'
                                }`}
                        >
                            {isAddressVerified ? (
                                <>
                                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                                    Verified
                                </>
                            ) : (
                                'Verify Address'
                            )}
                        </button>
                    </div>
                    {editError.message && (
                        <div className="rounded-lg bg-red-50 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-800">
                                        {editError.type}
                                    </h3>
                                    <p className="text-sm text-red-700 mt-1">
                                        {editError.message}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={!(isAddressVerified && editForm?.email && editForm?.phone && editForm?.name && editForm?.surname)}
                            className="inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </BaseModal>
        </div>
    );
};

export default PatientAbout;